import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaArrowLeft,
    FaUser,
    FaPhoneAlt,
    FaEnvelope,
    FaCommentDots,
    FaPaperPlane,
    FaWhatsapp,
    FaMapMarkerAlt,
    FaCheckCircle,
    FaExclamationTriangle,
    FaListUl,
    FaHeadset
} from "react-icons/fa";
// import api from "../api/Api"; // Uncomment when connecting to backend

export default function EnquiryPage() {
    const navigate = useNavigate();

    // --- STATE ---
    const [loading, setLoading] = useState(false);
    const [alertConfig, setAlertConfig] = useState({ show: false, type: "", message: "" });
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        email: "",
        subject: "General Inquiry",
        message: ""
    });

    // Patra Travels specific inquiry types
    const enquiryTypes = [
        "General Inquiry",
        "Bulk / Corporate Booking",
        "Outstation / Tour Package",
        "Attach Your Taxi (Vendor)",
        "Complaint / Feedback"
    ];

    // --- HANDLERS ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const triggerAlert = (type, msg) => {
        setAlertConfig({ show: true, type, message: msg });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic Validation
        if (!formData.name || !formData.phone || !formData.message) {
            triggerAlert("error", "Please fill in your Name, Phone Number, and Message.");
            return;
        }

        if (formData.phone.length < 10) {
            triggerAlert("error", "Please enter a valid 10-digit mobile number.");
            return;
        }

        setLoading(true);

        try {
            // Simulated API Call - Replace with your actual backend endpoint
            // const payload = { ...formData, source: 'App Enquiry' };
            // await api.post("/enquiry/create", payload);

            setTimeout(() => {
                setLoading(false);
                triggerAlert("success", "Your enquiry has been sent successfully. The Patra Travels team will contact you shortly!");
                setFormData({ name: "", phone: "", email: "", subject: "General Inquiry", message: "" }); // Clear form
            }, 1500);

        } catch (error) {
            console.error("Enquiry Error:", error);
            setLoading(false);
            triggerAlert("error", "Failed to send enquiry. Please try again later.");
        }
    };

    return (
        <div className="d-flex flex-column min-vh-100 font-sans overflow-hidden" style={{ backgroundColor: '#f5f5f7' }}>

            {/* --- NATIVE ALERT MODAL (Glassmorphism) --- */}
            {alertConfig.show && (
                <div className="native-alert-overlay" onClick={() => setAlertConfig({ show: false, type: "", message: "" })}>
                    <div className="native-alert-card animate-pop-in shadow-lg" onClick={e => e.stopPropagation()}>
                        <div className="p-4 text-center">
                            {alertConfig.type === "success" ? (
                                <div className="d-inline-flex bg-success bg-opacity-10 text-success rounded-circle p-3 mb-3">
                                    <FaCheckCircle size={36} />
                                </div>
                            ) : (
                                <div className="d-inline-flex bg-danger bg-opacity-10 text-danger rounded-circle p-3 mb-3">
                                    <FaExclamationTriangle size={36} />
                                </div>
                            )}
                            <h5 className="fw-bolder mb-2 text-dark" style={{ letterSpacing: '-0.3px' }}>
                                {alertConfig.type === "success" ? "Message Sent!" : "Missing Details"}
                            </h5>
                            <p className="text-muted small mb-4 px-2" style={{ fontSize: '0.85rem', lineHeight: '1.5' }}>
                                {alertConfig.message}
                            </p>
                            <button
                                className="btn w-100 rounded-pill py-3 fw-bold text-white shadow-sm btn-native-press"
                                style={{ 
                                    background: alertConfig.type === "success" ? 'linear-gradient(135deg, #198754, #20c997)' : 'linear-gradient(135deg, #ff3b30, #ff6961)', 
                                    fontSize: '1rem' 
                                }}
                                onClick={() => {
                                    setAlertConfig({ show: false, type: "", message: "" });
                                    if (alertConfig.type === "success") navigate(-1);
                                }}
                            >
                                Okay
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="container-fluid flex-grow-1 p-3 pb-5" style={{ maxWidth: '600px', margin: '0 auto' }}>

                {/* --- 1. Brand Welcome & Quick Contact Cards --- */}
                <div className="mb-4 animate-slide-up">
                    <div className="d-flex align-items-center gap-2 mb-3 px-1">
                        <FaHeadset color="#ff7a00" size={20} />
                        <h5 className="fw-bolder text-dark m-0" style={{ letterSpacing: '-0.5px' }}>Patra Travels Support</h5>
                    </div>

                    <div className="row g-3">
                        {/* CALL US BUTTON */}
                        <div className="col-6">
                            <a href="tel:+918337911111" className="text-decoration-none text-dark">
                                <div 
                                    className="bg-white rounded-4 p-3 border-0 d-flex flex-column align-items-center justify-content-center h-100 btn-native-press"
                                    style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}
                                >
                                    <div 
                                        className="text-white rounded-circle mb-2 d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '52px', height: '52px', 
                                            background: 'linear-gradient(135deg, #FF6A00 0%, #FFA800 100%)',
                                            boxShadow: '0 8px 16px rgba(255, 106, 0, 0.25)' 
                                        }}
                                    >
                                        <FaPhoneAlt size={20} />
                                    </div>
                                    <span className="fw-bold" style={{ fontSize: '0.9rem', letterSpacing: '-0.2px' }}>Call Us</span>
                                    <span className="text-muted" style={{ fontSize: '0.7rem', fontWeight: '500' }}>24/7 Helpline</span>
                                </div>
                            </a>
                        </div>

                        {/* WHATSAPP BUTTON */}
                        <div className="col-6">
                            <a href="https://wa.me/918337911111?text=Hi%20Patra%20Travels%20Support,%20I%20have%20an%20enquiry:" target="_blank" rel="noreferrer" className="text-decoration-none text-dark">
                                <div 
                                    className="bg-white rounded-4 p-3 border-0 d-flex flex-column align-items-center justify-content-center h-100 btn-native-press"
                                    style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}
                                >
                                    <div 
                                        className="text-white rounded-circle mb-2 d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '52px', height: '52px', 
                                            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', 
                                            boxShadow: '0 8px 16px rgba(37, 211, 102, 0.25)' 
                                        }}
                                    >
                                        <FaWhatsapp size={26} /> 
                                    </div>
                                    <span className="fw-bold" style={{ fontSize: '0.9rem', letterSpacing: '-0.2px' }}>WhatsApp</span>
                                    <span className="text-muted" style={{ fontSize: '0.7rem', fontWeight: '500' }}>Quick Reply</span>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>

                {/* --- 2. Enquiry Form --- */}
                <div className="bg-white rounded-4 border-0 p-4 animate-slide-up" style={{ animationDelay: '0.1s', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
                    <h5 className="fw-bolder text-dark mb-1" style={{ letterSpacing: '-0.3px' }}>Write to Us</h5>
                    <p className="text-muted mb-4" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>Drop your details below and our dispatch team will connect with you.</p>

                    <form onSubmit={handleSubmit}>

                        {/* Name */}
                        <div className="mb-3 position-relative focus-ring-container rounded-4 p-2 custom-input-bg">
                            <label className="text-muted fw-bold mb-1 ms-2 d-flex align-items-center" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <FaUser className="me-2 text-primary opacity-75" size={10} /> Full Name
                            </label>
                            <input
                                type="text" name="name"
                                className="form-control bg-transparent border-0 fw-bold text-dark px-2 pb-1 pt-0"
                                style={{ fontSize: '0.95rem', boxShadow: 'none' }}
                                placeholder="Enter your name"
                                value={formData.name} onChange={handleInputChange}
                            />
                        </div>

                        {/* Phone */}
                        <div className="mb-3 position-relative focus-ring-container rounded-4 p-2 custom-input-bg">
                            <label className="text-muted fw-bold mb-1 ms-2 d-flex align-items-center" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <FaPhoneAlt className="me-2 text-primary opacity-75" size={10} /> Phone Number
                            </label>
                            <input
                                type="tel" name="phone" inputMode="tel" maxLength="10"
                                className="form-control bg-transparent border-0 fw-bold text-dark px-2 pb-1 pt-0"
                                style={{ fontSize: '0.95rem', boxShadow: 'none' }}
                                placeholder="10-digit mobile number"
                                value={formData.phone} onChange={handleInputChange}
                            />
                        </div>

                        {/* Email */}
                        <div className="mb-3 position-relative focus-ring-container rounded-4 p-2 custom-input-bg">
                            <label className="text-muted fw-bold mb-1 ms-2 d-flex align-items-center" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <FaEnvelope className="me-2 text-primary opacity-75" size={10} /> Email Address <span className="text-muted ms-1 text-lowercase" style={{ fontSize: '0.55rem' }}>(Optional)</span>
                            </label>
                            <input
                                type="email" name="email" inputMode="email"
                                className="form-control bg-transparent border-0 fw-bold text-dark px-2 pb-1 pt-0"
                                style={{ fontSize: '0.95rem', boxShadow: 'none' }}
                                placeholder="example@mail.com"
                                value={formData.email} onChange={handleInputChange}
                            />
                        </div>

                        {/* Subject Category */}
                        <div className="mb-3 position-relative focus-ring-container rounded-4 p-2 custom-input-bg cursor-pointer">
                            <label className="text-muted fw-bold mb-1 ms-2 d-flex align-items-center cursor-pointer" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <FaListUl className="me-2 text-primary opacity-75" size={10} /> Enquiry Type
                            </label>
                            <select
                                name="subject"
                                className="form-select bg-transparent border-0 fw-bold text-dark px-2 pb-1 pt-0 cursor-pointer custom-select-arrow"
                                style={{ fontSize: '0.95rem', boxShadow: 'none' }}
                                value={formData.subject} onChange={handleInputChange}
                            >
                                {enquiryTypes.map((type, i) => (
                                    <option key={i} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>

                        {/* Message */}
                        <div className="mb-4 position-relative focus-ring-container rounded-4 p-2 custom-input-bg">
                            <label className="text-muted fw-bold mb-1 ms-2 d-flex align-items-center" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <FaCommentDots className="me-2 text-primary opacity-75" size={10} /> Your Message
                            </label>
                            <textarea
                                name="message" rows="3"
                                className="form-control bg-transparent border-0 fw-bold text-dark px-2 pb-1 pt-0"
                                style={{ fontSize: '0.95rem', boxShadow: 'none', resize: 'none' }}
                                placeholder="How can we help you today?"
                                value={formData.message} onChange={handleInputChange}
                            ></textarea>
                        </div>

                        {/* Submit Button - Branded Patra Travels Orange */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn w-100 py-3 fw-bold text-white rounded-pill btn-native-press d-flex align-items-center justify-content-center gap-2"
                            style={{ 
                                background: 'linear-gradient(135deg, #ff7a00, #ff9500)', 
                                fontSize: '1.05rem',
                                boxShadow: '0 8px 20px rgba(255, 122, 0, 0.3)' 
                            }}
                        >
                            {loading ? (
                                <span className="spinner-border spinner-border-sm" />
                            ) : (
                                <>
                                    <FaPaperPlane size={14} /> Send Enquiry
                                </>
                            )}
                        </button>

                    </form>
                </div>

                {/* --- 3. Office Address --- */}
                <div className="mt-4 p-3 d-flex align-items-start gap-3 animate-slide-up bg-white rounded-4 border-0" style={{ animationDelay: '0.2s', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                    <div className="bg-danger bg-opacity-10 p-3 rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center">
                        <FaMapMarkerAlt className="text-danger" size={18} />
                    </div>
                    <div>
                        <h6 className="fw-bolder text-dark mb-1" style={{ fontSize: '0.9rem', letterSpacing: '-0.2px' }}>Patra Travels Head Office</h6>
                        <p className="text-muted m-0" style={{ fontSize: '0.8rem', lineHeight: '1.5' }}>
                            1st Floor, Royal Palace Campus, Plot No. 1151, <br />
                            Tankapani Rd, Bhubaneswar, Odisha 751018 <br />
                            <a href="mailto:support@patratravels.com" className="fw-bold text-decoration-none mt-2 d-inline-block" style={{ color: '#ff7a00' }}>support@patratravels.com</a>
                        </p>
                    </div>
                </div>

            </div>

            <style>{`
                /* General Utilities */
                .cursor-pointer { cursor: pointer; }
                
                /* Input Backgrounds (iOS style gray) */
                .custom-input-bg {
                    background-color: #f4f4f5;
                    border: 1.5px solid transparent;
                    transition: all 0.2s ease;
                }

                /* Focus Ring Animation */
                .focus-ring-container:focus-within {
                    border-color: #ff7a00 !important;
                    background-color: #ffffff !important;
                    box-shadow: 0 4px 16px rgba(255, 122, 0, 0.08);
                }

                /* Haptic Button Press */
                .btn-native-press { 
                    transition: transform 0.1s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.1s ease; 
                }
                .btn-native-press:active { 
                    transform: scale(0.96); 
                    box-shadow: none !important; 
                }

                /* Alert Modal with Glassmorphism */
                .native-alert-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.4); 
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    z-index: 10000;
                    display: flex; align-items: center; justify-content: center; padding: 24px;
                    animation: fadeIn 0.2s ease-out;
                }
                .native-alert-card {
                    background: white; border-radius: 24px;
                    width: 100%; max-width: 320px;
                }

                /* Animations */
                @keyframes slideUpFade {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .animate-slide-up { opacity: 0; animation: slideUpFade 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                
                @keyframes popIn { 
                    from { transform: scale(0.9); opacity: 0; } 
                    to { transform: scale(1); opacity: 1; } 
                }
                .animate-pop-in { animation: popIn 0.2s cubic-bezier(0.16, 1, 0.3, 1); }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                /* Fix iOS Form Elements */
                .form-control::placeholder { color: #a1a1aa; font-weight: 500; }
                .form-control, .form-select { 
                    -webkit-appearance: none; 
                    color: #1c1c1e !important;
                }
                
                /* Custom Select Arrow */
                .custom-select-arrow {
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
                    background-repeat: no-repeat;
                    background-position: right 0.75rem center;
                    background-size: 16px 12px;
                }
            `}</style>
        </div>
    );
}